import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Ban, Brain, ChevronLeft, ChevronRight, FileText, Loader2, MessageCircle, Search, ThumbsDown, ThumbsUp, Wrench, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/App";

// Panel admin > Vanta AI > Conversations : toutes les conversations, recherche par
// titre, email ou pseudo, et lecture seule d'une conversation. Chaque ouverture est
// notée côté serveur (qui, quand).

const MODEL_NAMES = { pulse: "Pulse", vector: "Vector" };
const EFFORT_NAMES = { minimal: "Minimal", moyen: "Moyen", eleve: "Élevé", tres_eleve: "Très élevé", ultra: "Ultra" };
const PAGE = 50;
const errorText = (err, fallback) => err?.response?.data?.detail || fallback;
const when = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "");
const ARTIFACT = /<artifact\b([^>]*)>([\s\S]*?)(<\/artifact>|$)/gi;

// Le texte d'une réponse, avec les artefacts repliés (titre visible, contenu au clic).
function AnswerText({ text }) {
  const parts = [];
  let last = 0;
  for (const m of text.matchAll(ARTIFACT)) {
    if (m.index > last) parts.push(<p key={last} className="whitespace-pre-wrap break-words">{text.slice(last, m.index)}</p>);
    const title = /title="([^"]*)"/.exec(m[1])?.[1] || "Artefact";
    parts.push(
      <details key={m.index} className="my-2 rounded-lg border border-white/10 bg-black/30">
        <summary className="cursor-pointer px-3 py-2 text-xs text-white/70 flex items-center gap-2"><FileText className="w-3.5 h-3.5" />Artefact : {title}</summary>
        <pre className="px-3 pb-3 text-[11px] text-white/70 overflow-x-auto max-h-80">{m[2].trim()}</pre>
      </details>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(<p key={last} className="whitespace-pre-wrap break-words">{text.slice(last)}</p>);
  return <>{parts}</>;
}

function Bubble({ msg }) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl bg-white/[0.07] px-4 py-2.5 text-sm">
          <p className="whitespace-pre-wrap break-words">{msg.content}</p>
          {(msg.attachments || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">{msg.attachments.map((a) => <span key={a.id} className="text-[11px] px-2 py-0.5 rounded bg-black/30 text-white/60">📎 {a.name}</span>)}</div>
          )}
          <p className="text-[10px] text-white/35 mt-1 text-right">{when(msg.created_at)}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="text-sm text-white/85 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/40">
        <span className="text-white/60">{MODEL_NAMES[msg.model] || msg.model}</span>
        {msg.effort && <span>· {EFFORT_NAMES[msg.effort] || msg.effort}</span>}
        <span>· {when(msg.created_at)}</span>
        {msg.refused && <span className="text-amber-300 flex items-center gap-1"><Ban className="w-3 h-3" />refusé</span>}
        {msg.rating === "up" && <span className="text-emerald-300 flex items-center gap-1"><ThumbsUp className="w-3 h-3" />{msg.rating_note}</span>}
        {msg.rating === "down" && <span className="text-red-300 flex items-center gap-1"><ThumbsDown className="w-3 h-3" />{msg.rating_note}</span>}
      </div>
      {(msg.blocks || []).map((b, i) => {
        if (b.type === "text") return <AnswerText key={i} text={b.text || ""} />;
        if (b.type === "thinking") return (
          <details key={i} className="text-xs text-white/50"><summary className="cursor-pointer flex items-center gap-1.5"><Brain className="w-3.5 h-3.5" />Réflexion</summary><p className="whitespace-pre-wrap mt-1 pl-5">{b.text}</p></details>
        );
        if (b.type === "tool") return <p key={i} className="text-xs text-white/45 flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5" />Outil : {b.name}</p>;
        return null;
      })}
      {msg.error && <p className="text-xs text-red-300">{msg.error}</p>}
    </div>
  );
}

function Reader({ chatId, hdr, onBack }) {
  const [chat, setChat] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/vai/chats/${chatId}`, { headers: hdr() }).then((r) => setChat(r.data))
      .catch((err) => { toast.error(errorText(err, "Conversation introuvable")); onBack(); });
  }, [chatId, hdr, onBack]);
  if (!chat) return <p className="text-sm text-white/40 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</p>;
  return (
    <div className="space-y-4" data-testid="vai-admin-reader">
      <div className="flex items-start gap-3">
        <Button size="sm" variant="outline" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1" />Retour</Button>
        <div className="min-w-0">
          <p className="font-medium truncate">{chat.title}</p>
          <p className="text-xs text-white/45 truncate">{chat.username || chat.email} · {chat.email} · {MODEL_NAMES[chat.model] || chat.model} · {chat.messages.length} message{chat.messages.length > 1 ? "s" : ""}</p>
        </div>
      </div>
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-5">
        {chat.messages.length === 0 ? <p className="text-sm text-white/40">Conversation vide.</p> : chat.messages.map((m) => <Bubble key={m.id} msg={m} />)}
      </div>
    </div>
  );
}

export default function VantaAIChats({ hdr, user, onClearUser }) {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => axios.get(`${API}/admin/vai/chats`, { headers: hdr(), params: { q, skip, user_id: user?.user_id || "" } })
    .then((r) => { setRows(r.data.chats); setTotal(r.data.total); })
    .catch((err) => toast.error(errorText(err, "Chargement impossible"))), [hdr, q, skip, user]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);
  useEffect(() => { setSkip(0); }, [q, user]);
  const back = useCallback(() => setOpen(null), []);

  if (open) return <Reader chatId={open} hdr={hdr} onBack={back} />;
  return (
    <div className="space-y-4" data-testid="vai-admin-chats">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><MessageCircle className="w-5 h-5" />Conversations</h2>
          <p className="text-sm text-white/50 mt-1">Toutes les conversations avec Vanta AI, en lecture seule. Chaque ouverture est enregistrée.</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-white/30" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Titre, email ou pseudo" className="pl-8 w-64" data-testid="vai-admin-chats-search" />
        </div>
      </div>
      {user && (
        <div className="inline-flex items-center gap-2 rounded-lg border border-violet-500/30 bg-violet-500/10 px-3 py-1.5 text-xs text-violet-100">
          Conversations de {user.username || user.email}
          <button type="button" onClick={onClearUser} aria-label="Voir toutes les conversations"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
      {rows === null ? <p className="text-sm text-white/40 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</p>
        : rows.length === 0 ? <p className="text-sm text-white/40">Aucune conversation{q ? " trouvée" : ""}.</p> : (
          <div className="divide-y divide-white/5 border border-white/10 rounded-xl overflow-hidden">
            {rows.map((c) => (
              <button key={c.id} type="button" onClick={() => setOpen(c.id)} className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-white/[0.03]" data-testid={`vai-admin-chat-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm truncate">{c.title}</p>
                  <p className="text-xs text-white/45 truncate">{c.username || c.email} · {c.email}</p>
                </div>
                <span className="text-[11px] px-1.5 py-0.5 rounded border border-white/10 text-white/55 shrink-0">{MODEL_NAMES[c.model] || c.model}</span>
                <span className="text-xs text-white/40 shrink-0 hidden sm:block">{when(c.updated_at)}</span>
              </button>
            ))}
          </div>
        )}
      {total > PAGE && (
        <div className="flex items-center justify-between text-xs text-white/50">
          <span>{skip + 1}–{Math.min(skip + PAGE, total)} sur {total}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))}><ChevronLeft className="w-4 h-4" /></Button>
            <Button size="sm" variant="outline" disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
